import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Chip,
  Icon,
  Loader,
  MenuMulti,
  type MenuMultiOption,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import {
  useDebounceForFunctions,
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import {
  type AccountRole,
  type IdentityAccountRow,
  type RoleAuthObject,
  useLazySearchAccountRolesQuery,
  useLazySearchRoleAuthObjectsQuery,
  useSearchIdentityAccountsQuery,
  useSearchIdentityRolesQuery,
} from '../../../../data-layer/licenseManagementApi';
import type { FUEInventoryRow } from '../FUEInventoryTable/constants/fueInventoryTableConstants';
import { useInfiniteScroll } from '../FUEInventoryTable/hooks/useInfiniteScroll';

import './FUEInventoryUserPanel.css';

interface FUEInventoryUserPanelProps {
  isOpen: boolean;
  onClose: () => void;
  user: FUEInventoryRow | null;
  vendor: string;
}

/** Maps a tier name to the CSS class that applies its exact hex color. */
function getTierChipClass(tier: string): string {
  if (!tier) {
    return 'LicenseManagement-tierChip--unknown';
  }

  if (tier.startsWith('Advanced')) {
    return 'LicenseManagement-tierChip--advanced';
  }

  if (tier.startsWith('Core')) {
    return 'LicenseManagement-tierChip--core';
  }

  if (tier.startsWith('Self-Service')) {
    return 'LicenseManagement-tierChip--selfService';
  }

  if (tier.startsWith('Developer')) {
    return 'LicenseManagement-tierChip--developer';
  }

  return 'LicenseManagement-tierChip--unknown';
}

/** Breadcrumb path for the role detail drill-down */
interface RoleDrilldownPath {
  accountName: string;
  roleName: string;
  roleKey: number;
  parentRoleName?: string;
}

/** Role Auth Objects drill-down view with breadcrumb navigation and infinite scroll */
function RoleAuthObjectsView({
  drilldownPath,
  vendor,
  onBack,
}: {
  drilldownPath: RoleDrilldownPath;
  vendor: string;
  onBack: () => void;
}) {
  const localize = useInvertedLocalize();
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );

  const [allAuthObjects, setAllAuthObjects] = useState<RoleAuthObject[]>([]);
  const [nextSearchAfter, setNextSearchAfter] = useState<string[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFirstPageLoading, setIsFirstPageLoading] = useState(false);
  const [fetchMore, { isFetching: isFetchingMore }] =
    useLazySearchRoleAuthObjectsQuery();
  const firstPageLoaded = useRef(false);

  // Load first page on mount
  useEffect(() => {
    if (firstPageLoaded.current) {
      return;
    }

    firstPageLoaded.current = true;
    setIsFirstPageLoading(true);

    fetchMore({
      vendor,
      roleKey: drilldownPath.roleKey,
      cloudLicenseKey,
      searchAfter: null,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          setAllAuthObjects(result.data.authObjects ?? []);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsFirstPageLoading(false);
      });
  }, [fetchMore, vendor, drilldownPath.roleKey, cloudLicenseKey]);

  const loadNextPage = useCallback(() => {
    if (!nextSearchAfter || isFetchingMore) {
      return;
    }

    fetchMore({
      vendor,
      roleKey: drilldownPath.roleKey,
      cloudLicenseKey,
      searchAfter: nextSearchAfter,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          const newRows = result.data.authObjects ?? [];

          setAllAuthObjects((prev) => [...prev, ...newRows]);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {});
  }, [
    nextSearchAfter,
    isFetchingMore,
    fetchMore,
    vendor,
    drilldownPath.roleKey,
    cloudLicenseKey,
  ]);

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: loadNextPage,
  });

  return (
    <div className='LicenseManagement-FUEUserPanel-drilldown'>
      {/* Back navigation */}
      <button
        type='button'
        className='LicenseManagement-FUEUserPanel-drilldown-back'
        onClick={onBack}>
        <Icon kind='ChevronLeft' size='xSmall' />
        <Typography
          kind='body3'
          className='LicenseManagement-FUEUserPanel-drilldown-backText'>
          {localize('Back to roles')}
        </Typography>
      </button>

      {/* Path context */}
      <div className='LicenseManagement-FUEUserPanel-drilldown-path'>
        <Typography
          kind='body3'
          className='LicenseManagement-FUEUserPanel-drilldown-pathText'>
          {[
            drilldownPath.accountName,
            drilldownPath.parentRoleName,
            drilldownPath.roleName,
          ]
            .filter(Boolean)
            .join('  >  ')}
        </Typography>
      </div>

      {/* Header */}
      <div className='LicenseManagement-FUEUserPanel-drilldown-header'>
        <Typography kind='body2-bold'>{drilldownPath.roleName}</Typography>
        <Typography
          kind='body3'
          className='LicenseManagement-FUEUserPanel-drilldown-count'>
          {allAuthObjects.length} {localize('auth objects')}
        </Typography>
      </div>

      {/* Table header */}
      <div className='LicenseManagement-FUEUserPanel-drilldown-tableHeader'>
        <div className='LicenseManagement-FUEUserPanel-drilldown-colAuthObj'>
          <Typography
            kind='body3'
            className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
            {localize('AUTH OBJECT')}
          </Typography>
        </div>
        <div className='LicenseManagement-FUEUserPanel-drilldown-colField'>
          <Typography
            kind='body3'
            className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
            {localize('FIELD')}
          </Typography>
        </div>
        <div className='LicenseManagement-FUEUserPanel-drilldown-colValue'>
          <Typography
            kind='body3'
            className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
            {localize('VALUE')}
          </Typography>
        </div>
      </div>

      {/* Loading state */}
      {isFirstPageLoading && (
        <div className='LicenseManagement-FUEUserPanel-loader'>
          <Loader kind='loop' format='inline' color='brand' />
        </div>
      )}

      {/* Auth object rows */}
      {!isFirstPageLoading && allAuthObjects.length === 0 && (
        <Typography
          kind='body2'
          className='LicenseManagement-FUEUserPanel-noRoles'>
          {localize('No auth objects found.')}
        </Typography>
      )}

      {allAuthObjects.map((authObj) => (
        <div
          key={authObj.authObjectKey}
          className='LicenseManagement-FUEUserPanel-drilldown-row'>
          <div className='LicenseManagement-FUEUserPanel-drilldown-colAuthObj'>
            <Typography kind='body2'>
              <span title={authObj.authObjectName}>
                {authObj.authObjectName}
              </span>
            </Typography>
            {authObj.fields.length > 1 && (
              <Typography
                kind='body3'
                className='LicenseManagement-FUEUserPanel-drilldown-fieldCount'>
                {authObj.fields.length} {localize('fields')}
              </Typography>
            )}
          </div>
          <div className='LicenseManagement-FUEUserPanel-drilldown-colField'>
            {authObj.fields.map((f) => (
              <Typography
                key={`${authObj.authObjectKey}-field-${f.field}-${f.value}`}
                kind='body3'
                className='LicenseManagement-FUEUserPanel-drilldown-fieldText'>
                <span title={f.field}>{f.field}</span>
              </Typography>
            ))}
          </div>
          <div className='LicenseManagement-FUEUserPanel-drilldown-colValue'>
            {authObj.fields.map((f) => (
              <Typography
                key={`${authObj.authObjectKey}-value-${f.field}-${f.value}`}
                kind='body3'
                className='LicenseManagement-FUEUserPanel-drilldown-valueText'>
                <span title={f.value}>{f.value}</span>
              </Typography>
            ))}
          </div>
        </div>
      ))}

      {/* Sentinel for infinite scroll */}
      <div
        ref={sentinelRef}
        className='LicenseManagement-FUEUserPanel-sentinel'>
        {isFetchingMore && <Loader kind='loop' format='inline' color='brand' />}
      </div>
    </div>
  );
}

function AccountGroup({
  account,
  vendor,
  defaultExpanded = false,
  searchQuery = '',
  onRoleDrilldown,
}: {
  account: IdentityAccountRow;
  vendor: string;
  defaultExpanded?: boolean;
  searchQuery?: string;
  onRoleDrilldown?: (path: RoleDrilldownPath) => void;
}) {
  const localize = useInvertedLocalize();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );

  // Drill-down: notify parent to show auth objects view
  const handleRoleClick = (role: AccountRole, parentRoleName?: string) => {
    if (onRoleDrilldown) {
      onRoleDrilldown({
        accountName: account.accountName,
        roleName: role.roleName,
        roleKey: role.roleKey,
        parentRoleName,
      });
    }
  };

  // ── Infinite scroll (same pattern as FUEInventoryTable) ─────────────────────
  const [allRoles, setAllRoles] = useState<AccountRole[]>([]);
  const [nextSearchAfter, setNextSearchAfter] = useState<string[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFirstPageLoading, setIsFirstPageLoading] = useState(false);
  const [fetchMore, { isFetching: isFetchingMore }] =
    useLazySearchAccountRolesQuery();
  const firstPageLoaded = useRef(false);

  // Load first page when expanded
  useEffect(() => {
    if (!isExpanded || firstPageLoaded.current) {
      return;
    }

    firstPageLoaded.current = true;
    setIsFirstPageLoading(true);

    fetchMore({
      vendor,
      accountKey: account.accountKey,
      cloudLicenseKey,
      searchAfter: null,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          const rows = result.data.roles ?? [];

          setAllRoles(rows);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsFirstPageLoading(false);
      });
  }, [isExpanded, fetchMore, vendor, account.accountKey, cloudLicenseKey]);

  // Reset when collapsed
  useEffect(() => {
    if (!isExpanded) {
      setAllRoles([]);
      setNextSearchAfter(null);
      setHasMore(false);
      firstPageLoaded.current = false;
    }
  }, [isExpanded]);

  const loadNextPage = useCallback(() => {
    if (!nextSearchAfter || isFetchingMore) {
      return;
    }

    fetchMore({
      vendor,
      accountKey: account.accountKey,
      cloudLicenseKey,
      searchAfter: nextSearchAfter,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          const newRows = result.data.roles ?? [];

          setAllRoles((prev) => [...prev, ...newRows]);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {});
  }, [
    nextSearchAfter,
    isFetchingMore,
    fetchMore,
    vendor,
    account.accountKey,
    cloudLicenseKey,
  ]);

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: loadNextPage,
  });

  const totalCount = allRoles.length;

  const [expandedRoles, setExpandedRoles] = useState<Set<number>>(new Set());

  const toggleRole = (roleKey: number) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);

      if (next.has(roleKey)) {
        next.delete(roleKey);
      } else {
        next.add(roleKey);
      }

      return next;
    });
  };

  const filteredRoles = searchQuery
    ? allRoles.filter((r) =>
        r.roleName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allRoles;

  const renderAccountRoles = (): React.ReactElement => {
    if (isFirstPageLoading) {
      return (
        <div className='LicenseManagement-FUEUserPanel-loader'>
          <Loader kind='loop' format='inline' color='brand' />
        </div>
      );
    }

    if (!isFirstPageLoading && filteredRoles.length === 0) {
      return (
        <Typography
          kind='body2'
          className='LicenseManagement-FUEUserPanel-noRoles'>
          {localize('No roles found.')}
        </Typography>
      );
    }

    return (
      <>
        <div className='LicenseManagement-FUEUserPanel-rolesHeader'>
          <div className='LicenseManagement-FUEUserPanel-rolesHeaderCell'>
            <Typography
              kind='body3'
              className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
              {localize('ASSIGNED ROLE')}
            </Typography>
          </div>
          <div className='LicenseManagement-FUEUserPanel-rolesHeaderCell'>
            <Typography
              kind='body3'
              className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
              {localize('FUE SCORE')}
            </Typography>
          </div>
          <div className='LicenseManagement-FUEUserPanel-rolesHeaderCell'>
            <Typography
              kind='body3'
              className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
              {' '}
            </Typography>
          </div>
        </div>
        {filteredRoles.map((role) => {
          const isComposite =
            role.roleType === 'Composite' && role.childRoles.length > 0;
          const isRoleExpanded = expandedRoles.has(role.roleKey);
          const subRolesLabel = isComposite
            ? `${role.childRoles.length} ${localize('sub-roles')}`
            : '';

          return (
            <React.Fragment key={role.roleKey}>
              {isComposite ? (
                <button
                  type='button'
                  className='LicenseManagement-FUEUserPanel-roleRow LicenseManagement-FUEUserPanel-roleRow--composite'
                  onClick={() => toggleRole(role.roleKey)}>
                  <div className='LicenseManagement-FUEUserPanel-roleNameGroup'>
                    <Icon
                      kind={isRoleExpanded ? 'ChevronDown' : 'ChevronRight'}
                      size='xSmall'
                      className='LicenseManagement-FUEUserPanel-roleChevron'
                    />
                    <Typography
                      kind='body2'
                      className='LicenseManagement-FUEUserPanel-roleName'>
                      <span title={role.roleName}>{role.roleName}</span>
                    </Typography>
                  </div>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-fueScore'>
                    {role.tierWeight}
                  </Typography>
                  <div className='LicenseManagement-FUEUserPanel-roleDetail'>
                    <Typography
                      kind='body3'
                      className='LicenseManagement-FUEUserPanel-subRolesText'>
                      {subRolesLabel}
                    </Typography>
                  </div>
                </button>
              ) : (
                <div className='LicenseManagement-FUEUserPanel-roleRow'>
                  <div className='LicenseManagement-FUEUserPanel-roleNameGroup'>
                    <Typography
                      kind='body2'
                      className='LicenseManagement-FUEUserPanel-roleName'>
                      <span title={role.roleName}>{role.roleName}</span>
                    </Typography>
                  </div>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-fueScore'>
                    {role.tierWeight}
                  </Typography>
                  <button
                    type='button'
                    className='LicenseManagement-FUEUserPanel-roleDetail LicenseManagement-FUEUserPanel-roleDetail--clickable'
                    onClick={() => handleRoleClick(role)}>
                    <Icon
                      kind='ChevronRight'
                      size='small'
                      className='LicenseManagement-FUEUserPanel-roleArrowIcon'
                    />
                  </button>
                </div>
              )}
              {/* Child roles for composite */}
              {isComposite && isRoleExpanded && (
                <div className='LicenseManagement-FUEUserPanel-childRoles'>
                  {role.childRoles.map((child) => (
                    <div
                      key={child.roleKey}
                      className='LicenseManagement-FUEUserPanel-roleRow LicenseManagement-FUEUserPanel-roleRow--child'>
                      <div className='LicenseManagement-FUEUserPanel-roleNameGroup'>
                        <Typography
                          kind='body2'
                          className='LicenseManagement-FUEUserPanel-roleName'>
                          <span title={child.roleName}>{child.roleName}</span>
                        </Typography>
                      </div>
                      <Typography
                        kind='body2'
                        className='LicenseManagement-FUEUserPanel-fueScore'>
                        {child.tierWeight}
                      </Typography>
                      <button
                        type='button'
                        className='LicenseManagement-FUEUserPanel-roleDetail LicenseManagement-FUEUserPanel-roleDetail--clickable'
                        onClick={() =>
                          handleRoleClick(
                            {
                              ...child,
                              roleType: 'Single',
                              entitlementUsed: false,
                              childRoles: [],
                            } as AccountRole,
                            role.roleName
                          )
                        }>
                        <Icon
                          kind='ChevronRight'
                          size='small'
                          className='LicenseManagement-FUEUserPanel-roleArrowIcon'
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
        {/* Sentinel for infinite scroll */}
        <div
          ref={sentinelRef}
          className='LicenseManagement-FUEUserPanel-sentinel'>
          {isFetchingMore && (
            <Loader kind='loop' format='inline' color='brand' />
          )}
        </div>
      </>
    );
  };

  return (
    <div className='LicenseManagement-FUEUserPanel-endpointGroup'>
      <button
        type='button'
        className='LicenseManagement-FUEUserPanel-endpointHeader'
        onClick={() => setIsExpanded((prev) => !prev)}>
        <div className='LicenseManagement-FUEUserPanel-endpointInfo'>
          <Typography kind='body2-bold'>{account.accountName}</Typography>
          <Typography
            kind='body3'
            className='LicenseManagement-FUEUserPanel-endpointMeta'>
            {account.endpointName}
            {totalCount > 0
              ? ` • ${totalCount} ${localize('roles')} • Max FUE ${
                  account.highestTierWeight
                }`
              : ` • Max FUE ${account.highestTierWeight}`}
          </Typography>
        </div>
        <div className='LicenseManagement-FUEUserPanel-endpointRight'>
          {account.currentTier ? (
            <Chip
              kind='subtleFilled'
              color='warning'
              label={account.currentTier}
              size='small'
              className={getTierChipClass(account.currentTier)}
            />
          ) : (
            <Typography kind='body2'>{localize('—')}</Typography>
          )}
          <Icon
            kind={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size='small'
            className='LicenseManagement-FUEUserPanel-chevron'
          />
        </div>
      </button>

      {isExpanded && (
        <div className='LicenseManagement-FUEUserPanel-rolesTable'>
          {renderAccountRoles()}
        </div>
      )}
    </div>
  );
}

AccountGroup.defaultProps = {
  defaultExpanded: false,
  searchQuery: '',
  onRoleDrilldown: undefined,
};

type SearchMode = 'accounts' | 'roles';

/**
 *
 */
function FUEInventoryUserPanel({
  isOpen,
  onClose,
  user,
  vendor,
}: FUEInventoryUserPanelProps): React.ReactElement | null {
  const localize = useInvertedLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const [apiSearchText, setApiSearchText] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('accounts');
  const [panelDrilldown, setPanelDrilldown] =
    useState<RoleDrilldownPath | null>(null);
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );

  // Reset all state when panel closes or a different user is selected
  useEffect(() => {
    if (!isOpen) {
      setPanelDrilldown(null);
      setSearchQuery('');
      setApiSearchText('');
      setSearchMode('accounts');
    }
  }, [isOpen]);

  useEffect(() => {
    setPanelDrilldown(null);
    setSearchQuery('');
    setApiSearchText('');
    setSearchMode('accounts');
  }, [user]);

  const debouncedSetApiSearchText = useDebounceForFunctions(
    (query: string) => setApiSearchText(query),
    400
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;

    setSearchQuery(query);
    debouncedSetApiSearchText(query);
  };

  const handleModeSelect = (mode: SearchMode) => {
    setSearchMode(mode);
    setSearchQuery('');
    setApiSearchText('');
  };

  const { data: accountsData, isFetching: isAccountsFetching } =
    useSearchIdentityAccountsQuery(
      {
        vendor,
        identityId: user?.id ?? '',
        identityType: user?.identityType ?? '',
        cloudLicenseKey,
        searchText: apiSearchText || null,
      },
      { skip: !isOpen || !user || searchMode !== 'accounts' }
    );

  const { data: rolesData, isFetching: isRolesFetching } =
    useSearchIdentityRolesQuery(
      {
        vendor,
        identityId: user?.id ?? '',
        identityType: user?.identityType ?? '',
        cloudLicenseKey,
        searchText: apiSearchText || null,
      },
      { skip: !isOpen || !user || searchMode !== 'roles' }
    );

  if (!user) {
    return null;
  }

  const accounts: IdentityAccountRow[] = accountsData?.results ?? [];
  const totalAccounts = accountsData?.totalRecords ?? 0;
  const identityRoles: AccountRole[] = rolesData?.roles ?? [];
  const totalRoles = rolesData?.totalRecords ?? 0;
  const sectionTitle =
    searchMode === 'accounts'
      ? `${localize('Accounts')} (${totalAccounts})`
      : `${localize('Roles')} (${totalRoles})`;

  const renderAccountsContent = (): React.ReactElement => {
    if (isAccountsFetching) {
      return (
        <div className='LicenseManagement-FUEUserPanel-loader'>
          <Loader kind='loop' format='inline' color='brand' />
        </div>
      );
    }

    if (accounts.length === 0) {
      return (
        <Typography
          kind='body2'
          className='LicenseManagement-FUEUserPanel-noRoles'>
          {localize('No accounts found.')}
        </Typography>
      );
    }

    return (
      <>
        {accounts.map((account) => (
          <AccountGroup
            key={account.accountKey}
            account={account}
            vendor={vendor}
            defaultExpanded={false}
            searchQuery={searchQuery}
            onRoleDrilldown={setPanelDrilldown}
          />
        ))}
      </>
    );
  };

  const renderRolesContent = (): React.ReactElement => {
    if (isRolesFetching) {
      return (
        <div className='LicenseManagement-FUEUserPanel-loader'>
          <Loader kind='loop' format='inline' color='brand' />
        </div>
      );
    }

    if (identityRoles.length === 0) {
      return (
        <Typography
          kind='body2'
          className='LicenseManagement-FUEUserPanel-noRoles'>
          {localize('No roles found.')}
        </Typography>
      );
    }

    return (
      <>
        <div className='LicenseManagement-FUEUserPanel-rolesHeader'>
          <div className='LicenseManagement-FUEUserPanel-rolesHeaderCell'>
            <Typography
              kind='body3'
              className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
              {localize('ASSIGNED ROLE')}
            </Typography>
          </div>
          <div className='LicenseManagement-FUEUserPanel-rolesHeaderCell'>
            <Typography
              kind='body3'
              className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
              {localize('FUE SCORE')}
            </Typography>
          </div>
          <div className='LicenseManagement-FUEUserPanel-rolesHeaderCell'>
            <Typography
              kind='body3'
              className='LicenseManagement-FUEUserPanel-rolesHeaderText'>
              {' '}
            </Typography>
          </div>
        </div>
        {identityRoles.map((role) => {
          const isComposite =
            role.roleType === 'Composite' && role.childRoles.length > 0;
          const subRolesLabel = isComposite
            ? `${role.childRoles.length} ${localize('sub-roles')}`
            : '';

          if (isComposite) {
            return (
              <div
                key={role.roleKey}
                className='LicenseManagement-FUEUserPanel-roleRow'>
                <div className='LicenseManagement-FUEUserPanel-roleNameGroup'>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-roleName'>
                    <span title={role.roleName}>{role.roleName}</span>
                  </Typography>
                </div>
                <Typography
                  kind='body2'
                  className='LicenseManagement-FUEUserPanel-fueScore'>
                  {role.tierWeight}
                </Typography>
                <div className='LicenseManagement-FUEUserPanel-roleDetail'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-subRolesText'>
                    {subRolesLabel}
                  </Typography>
                </div>
              </div>
            );
          }

          return (
            <div
              key={role.roleKey}
              className='LicenseManagement-FUEUserPanel-roleRow'>
              <div className='LicenseManagement-FUEUserPanel-roleNameGroup'>
                <Typography
                  kind='body2'
                  className='LicenseManagement-FUEUserPanel-roleName'>
                  <span title={role.roleName}>{role.roleName}</span>
                </Typography>
              </div>
              <Typography
                kind='body2'
                className='LicenseManagement-FUEUserPanel-fueScore'>
                {role.tierWeight}
              </Typography>
              <button
                type='button'
                className='LicenseManagement-FUEUserPanel-roleDetail LicenseManagement-FUEUserPanel-roleDetail--clickable'
                onClick={() =>
                  setPanelDrilldown({
                    accountName: user.userId,
                    roleName: role.roleName,
                    roleKey: role.roleKey,
                  })
                }>
                <Icon
                  kind='ChevronRight'
                  size='small'
                  className='LicenseManagement-FUEUserPanel-roleArrowIcon'
                />
              </button>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      header={{
        title: user.userId,
        headerIcon: (
          <div className='LicenseManagement-FUEUserPanel-avatar'>
            <Icon
              kind='User'
              size='medium'
              color='--color-foreground-neutral-medium'
              className='LicenseManagement-FUEUserPanel-avatarIcon'
            />
          </div>
        ),
      }}
      layout={
        <div className='LicenseManagement-FUEUserPanel'>
          {panelDrilldown && (
            <RoleAuthObjectsView
              drilldownPath={panelDrilldown}
              vendor={vendor}
              onBack={() => setPanelDrilldown(null)}
            />
          )}
          <div
            className='LicenseManagement-FUEUserPanel-body'
            style={{ display: panelDrilldown ? 'none' : undefined }}>
            {/* Basic Info Grid */}
            <div className='LicenseManagement-FUEUserPanel-section'>
              <div className='LicenseManagement-FUEUserPanel-grid'>
                <div className='LicenseManagement-FUEUserPanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {user.identityType === 'Human'
                      ? localize('User ID')
                      : localize('Account Name')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-value'>
                    {user.userId ?? '—'}
                  </Typography>
                </div>
                <div className='LicenseManagement-FUEUserPanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {localize('Department')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-value'>
                    {user.department ?? '—'}
                  </Typography>
                </div>
              </div>
              <div className='LicenseManagement-FUEUserPanel-divider' />
              <div className='LicenseManagement-FUEUserPanel-grid'>
                <div className='LicenseManagement-FUEUserPanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {localize('Identity Type')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-value'>
                    {user.identityType ?? '—'}
                  </Typography>
                </div>
                <div className='LicenseManagement-FUEUserPanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {localize('Current FUE')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-value'>
                    {user.evaluatedCost != null
                      ? `$${user.evaluatedCost.toLocaleString()}`
                      : '—'}
                  </Typography>
                </div>
              </div>
              <div className='LicenseManagement-FUEUserPanel-divider' />
              <div className='LicenseManagement-FUEUserPanel-grid'>
                <div className='LicenseManagement-FUEUserPanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {localize('Last Active')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-value'>
                    {user.lastActiveAt ?? '—'}
                  </Typography>
                </div>
                <div className='LicenseManagement-FUEUserPanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {localize('Days Inactive')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-FUEUserPanel-value'>
                    {user.daysInactive != null
                      ? `${user.daysInactive} ${localize('Days')}`
                      : '—'}
                  </Typography>
                </div>
              </div>
            </div>

            <div className='LicenseManagement-FUEUserPanel-divider' />

            {/* FUE Tier Analysis */}
            <div className='LicenseManagement-FUEUserPanel-section'>
              <Typography
                kind='body2-bold'
                className='LicenseManagement-FUEUserPanel-sectionTitle'>
                {localize('Aggregated FUE Tier Analysis')}
              </Typography>
              <div className='LicenseManagement-FUEUserPanel-grid LicenseManagement-FUEUserPanel-tierGrid'>
                <div className='LicenseManagement-FUEUserPanel-tierField'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {localize('Current Tier')}
                  </Typography>
                  {user.currentTier ? (
                    <Chip
                      kind='subtleFilled'
                      color='warning'
                      label={user.currentTier}
                      size='small'
                      className={getTierChipClass(user.currentTier)}
                    />
                  ) : (
                    <Typography kind='body2'>{localize('—')}</Typography>
                  )}
                </div>
                <div className='LicenseManagement-FUEUserPanel-tierField'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-FUEUserPanel-label'>
                    {localize('Recommended Tier')}
                  </Typography>
                  {user.recommendedTier ? (
                    <Chip
                      kind='subtleFilled'
                      color='warning'
                      label={user.recommendedTier}
                      size='small'
                      className={getTierChipClass(user.recommendedTier)}
                    />
                  ) : (
                    <Typography kind='body2'>{localize('—')}</Typography>
                  )}
                </div>
              </div>
            </div>

            <div className='LicenseManagement-FUEUserPanel-divider' />

            {/* Accounts / Roles section */}
            <div className='LicenseManagement-FUEUserPanel-section'>
              <Typography
                kind='body2-bold'
                className='LicenseManagement-FUEUserPanel-sectionTitle'>
                {sectionTitle}
              </Typography>

              {/* Unified search bar: outer border wrapper + DS MenuMulti + DS InputField (border stripped) */}
              <div className='LicenseManagement-FUEUserPanel-searchBar'>
                <MenuMulti
                  label=''
                  options={[
                    {
                      value: 'accounts',
                      label: localize('Accounts'),
                      isChecked: searchMode === 'accounts',
                    },
                    {
                      value: 'roles',
                      label: localize('Roles'),
                      isChecked: searchMode === 'roles',
                    },
                  ]}
                  isFilteringDisabled
                  minOptionsForFooter={3}
                  trigger={
                    <button
                      type='button'
                      className='LicenseManagement-FUEUserPanel-searchBar-modeTrigger'>
                      <span className='LicenseManagement-FUEUserPanel-searchBar-modeLabel'>
                        {searchMode === 'accounts'
                          ? localize('Accounts')
                          : localize('Roles')}
                      </span>
                      <Icon
                        kind='ChevronDown'
                        size='xSmall'
                        className='LicenseManagement-FUEUserPanel-searchBar-modeChevron'
                      />
                    </button>
                  }
                  onChange={(selected) => {
                    const newOpt = (selected ?? []).find(
                      (o: MenuMultiOption) =>
                        o.isChecked && o.value !== searchMode
                    );

                    if (newOpt) {
                      handleModeSelect(newOpt.value as SearchMode);
                    }
                  }}
                />
                <div className='LicenseManagement-FUEUserPanel-searchBar-divider' />
                <Icon
                  kind='Search'
                  size='small'
                  className='LicenseManagement-FUEUserPanel-searchBar-searchIcon'
                />
                <input
                  type='text'
                  className='LicenseManagement-FUEUserPanel-searchBar-input'
                  placeholder={
                    searchMode === 'accounts'
                      ? localize('Search accounts...')
                      : localize('Search roles...')
                  }
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>

              <div className='LicenseManagement-FUEUserPanel-endpoints'>
                {searchMode === 'accounts'
                  ? renderAccountsContent()
                  : renderRolesContent()}
              </div>
            </div>
          </div>

          {/* Footer - hidden during drilldown */}
          {!panelDrilldown && (
            <div className='LicenseManagement-FUEUserPanel-footer'>
              <Button kind='ghost' onClick={onClose}>
                {localize('Close')}
              </Button>
            </div>
          )}
        </div>
      }
      className='LicenseManagement-FUEUserPanel-sidePanel'
    />
  );
}

export default FUEInventoryUserPanel;
